import { ExpandMore as ExpandMoreIcon, Person as UserIcon } from "@mui/icons-material";
import { Box, Collapse, IconButton, SxProps, Theme, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import MobiliLogo from "../assets/Mobili web icon final 1-01.svg";

export type ChatMessageRole = "user" | "assistant" | "system";

export interface ChatMessage {
  /** Unique identifier for the message */
  id: string;
  /** Who sent the message */
  role: ChatMessageRole;
  /** Primary text content (always visible) */
  content: string;
  /** Optional collapsible content — rendered inside an accordion below the main content */
  collapsibleContent?: React.ReactNode;
  /** Label for the collapsible section header */
  collapsibleLabel?: string;
  /** Whether the collapsible section starts expanded */
  defaultExpanded?: boolean;
  /** Optional timestamp */
  timestamp?: Date;
  /** Whether to show a loading indicator (typing dots) instead of content */
  isLoading?: boolean;
  /** Optional icon override */
  icon?: React.ReactNode;
}

export interface ChatMessageBoxSlotProps {
  /** Override bubble styles per role. Merged with defaults. */
  bubble?: Partial<Record<ChatMessageRole, SxProps<Theme>>>;
  /** Override sx for the inner bubble Box (px, py, maxWidth). Merged with defaults. */
  bubbleBox?: SxProps<Theme>;
  /** Override sx for the content Typography */
  content?: SxProps<Theme>;
  /** Override sx for the timestamp Typography */
  timestamp?: SxProps<Theme>;
  /** Override sx for each message row container */
  messageRow?: SxProps<Theme>;
  /** Override avatar size in px. Default 28 */
  avatarSize?: number;
}

interface ChatMessageBoxProps {
  /** Array of messages to display */
  messages: ChatMessage[];
  /** Max height of the scrollable container. Defaults to 400 */
  maxHeight?: number | string;
  /** Additional sx overrides for the outer container */
  sx?: SxProps<Theme>;
  /** Optional avatar to render for user-role messages (overrides default) */
  userAvatar?: React.ReactNode;
  /** Style overrides for sub-components */
  slotProps?: ChatMessageBoxSlotProps;
}

const roleBubbleStyles: Record<ChatMessageRole, SxProps<Theme>> = {
  user: {
    bgcolor: "#e3f2fd",
    borderRadius: "16px 16px 4px 16px",
    alignSelf: "flex-end",
    ml: 6,
  },
  assistant: {
    bgcolor: "#f5f5f5",
    borderRadius: "16px 16px 16px 4px",
    alignSelf: "flex-start",
    mr: 6,
  },
  system: {
    bgcolor: "#fff3e0",
    borderRadius: "8px",
    alignSelf: "center",
    mx: 4,
  },
};

const roleAvatarColors: Record<ChatMessageRole, string> = {
  user: "#1976d2",
  assistant: "#7b1fa2",
  system: "#ed6c02",
};

const TypingDots = () => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "text.secondary",
          animation: "chatDotPulse 1.4s ease-in-out infinite",
          animationDelay: `${i * 0.2}s`,
          "@keyframes chatDotPulse": {
            "0%, 80%, 100%": { opacity: 0.3, transform: "scale(0.8)" },
            "40%": { opacity: 1, transform: "scale(1)" },
          },
        }}
      />
    ))}
  </Box>
);

const DefaultAvatar = ({ role, size = 28 }: { role: ChatMessageRole; size?: number }) => {
  const color = roleAvatarColors[role];
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: role === "assistant" ? "transparent" : color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {role === "assistant" ? (
        <img src={MobiliLogo} alt="Mobili" style={{ width: size, height: size }} />
      ) : (
        <UserIcon sx={{ fontSize: Math.round(size * 0.57), color: "#fff" }} />
      )}
    </Box>
  );
};

const MessageBubble: React.FC<{
  message: ChatMessage;
  userAvatar?: React.ReactNode;
  slotProps?: ChatMessageBoxSlotProps;
}> = ({ message, userAvatar, slotProps }) => {
  const [expanded, setExpanded] = useState(message.defaultExpanded ?? false);
  const isUser = message.role === "user";
  const isSystem = message.role === "system";

  const mergedBubbleRole = {
    ...roleBubbleStyles[message.role],
    ...slotProps?.bubble?.[message.role],
  } as SxProps<Theme>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 1,
        ...(isSystem && { justifyContent: "center" }),
        ...((slotProps?.messageRow ?? {}) as any),
      }}
    >
      {/* Avatar */}
      {!isSystem &&
        (message.icon ??
          (isUser && userAvatar ? (
            userAvatar
          ) : (
            <DefaultAvatar role={message.role} size={slotProps?.avatarSize} />
          )))}

      {/* Bubble */}
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          maxWidth: "85%",
          ...mergedBubbleRole,
          ...((slotProps?.bubbleBox ?? {}) as any),
        }}
      >
        {/* Main content */}
        {message.isLoading ? (
          <TypingDots />
        ) : (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              ...((slotProps?.content ?? {}) as any),
            }}
          >
            {message.content}
          </Typography>
        )}

        {/* Timestamp */}
        {message.timestamp && !message.isLoading && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              opacity: 0.6,
              fontSize: "0.65rem",
              ...((slotProps?.timestamp ?? {}) as any),
            }}
          >
            {message.timestamp.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        )}

        {/* Collapsible section */}
        {message.collapsibleContent && !message.isLoading && (
          <Box sx={{ mt: 1, borderTop: "1px solid", borderColor: "divider", pt: 0.5 }}>
            <Box
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, flex: 1 }}>
                {message.collapsibleLabel || "Details"}
              </Typography>
              <IconButton size="small" sx={{ p: 0 }}>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 18,
                    transition: "transform 0.2s",
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </IconButton>
            </Box>
            <Collapse in={expanded}>
              <Box sx={{ mt: 0.5 }}>{message.collapsibleContent}</Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ChatMessageBox: React.FC<ChatMessageBoxProps> = ({
  messages,
  maxHeight = 400,
  sx,
  userAvatar,
  slotProps,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        maxHeight,
        overflowY: "auto",
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        ...sx,
      }}
    >
      {messages.length === 0 && (
        <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
          Keine Nachrichten
        </Typography>
      )}
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} userAvatar={userAvatar} slotProps={slotProps} />
      ))}
    </Box>
  );
};

export default ChatMessageBox;
